import { useEffect, useMemo, useRef, useState } from "react"
import { masterUomFactor } from "helpers/uom-helper"
import _ from "lodash"
import nProgress from "nprogress"
import { useResultSet } from "repositories/db-repository"

/**
 * 200726: picker produk multi-select utk form pembelian — sebelumnya item hanya
 * bisa ditambah satu-satu per product unit via dropdown. Search sekali → centang
 * banyak unit (pilihan bertahan lintas halaman/pencarian) → isi qty → tambah
 * semua sekaligus. Sumber data = endpoint ps_list yang sama dengan dropdown lama.
 */
export default function ProductPickerModal({
  open,
  onClose,
  onAdd,
  outletId,
  supplierId,
  outletGroupId,
  // 290726 (permintaan user): satuan yang dipilih di form induk. Qty yang diketik
  // di modal ini SELALU dalam satuan tsb, jadi ditampilkan eksplisit supaya tidak
  // tertukar dengan kolom UOM bawaan produk (satuan master, sekadar informasi).
  uom = null,
}) {
  const selectedUom = String(uom || "").trim().toUpperCase()
  const searchInput = useRef(null)

  const [searchValue, setSearchValue] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  // Map pid -> { product, qty } — bertahan saat ganti halaman / kata kunci.
  const [selected, setSelected] = useState(() => new Map())

  const debouncedSetSearch = useMemo(
    () =>
      _.debounce((val) => {
        setSearch(val)
        setPage(1)
      }, 400),
    []
  )
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  useEffect(() => {
    if (!open) return
    setSearchValue("")
    setSearch("")
    setPage(1)
    setSelected(new Map())
    const t = setTimeout(() => searchInput.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [open])

  const params = useMemo(() => {
    // 240726 (#6): supplier_id OPSIONAL — dipakai form pembelian (butuh supplier),
    // tapi order HO memakai picker ini tanpa supplier (sumber = stok outlet HO).
    if (!open || !outletId) return null
    return {
      outlet_group_id: outletGroupId,
      outlet_id: outletId,
      ...(supplierId ? { supplier_id: supplierId } : {}),
      paginate: true,
      page,
      per_page: 15,
      search,
    }
  }, [open, outletId, supplierId, outletGroupId, page, search])

  const { data, isLoading } = useResultSet(
    params ? process.env.REACT_APP_INVENTORY_API_URL : null,
    "ps_list",
    params || {}
  )

  useEffect(() => {
    if (!open) return
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading, open])

  const rows = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data])

  const pidOf = (p) => String(p?.product_unit_id ?? "")
  const labelOf = (p) =>
    p?.product_unit_long_name || p?.product_unit || p?.sku || p?.product_model || pidOf(p) || "-"
  const descOf = (p) =>
    [
      p?.product_model,
      p?.sku ? `SKU: ${p.sku}` : null,
      p?.product_series,
      p?.product_brand,
      p?.product_type,
    ]
      .filter(Boolean)
      .join(" • ")

  // 290726 (permintaan user): stok jadi KOLOM sendiri dan ikut satuan terpilih.
  // current_stock dari server selalu PCS -> ÷ faktor satuan (2 desimal).
  const stockInUom = (p) => {
    const n = Number(p?.current_stock)
    if (!Number.isFinite(n)) return "-"
    const f = selectedUom ? masterUomFactor(selectedUom) : 1
    const v = Math.round((n / f) * 100) / 100
    return v.toLocaleString("id-ID")
  }

  const toggle = (p) => {
    const pid = pidOf(p)
    if (!pid) return
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(pid)) next.delete(pid)
      else next.set(pid, { product: p, qty: 1 })
      return next
    })
  }

  const setQty = (pid, qty) => {
    setSelected((prev) => {
      const next = new Map(prev)
      const cur = next.get(pid)
      if (cur) next.set(pid, { ...cur, qty })
      return next
    })
  }

  const allPageSelected = rows.length > 0 && rows.every((p) => selected.has(pidOf(p)))

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (allPageSelected) {
        for (const p of rows) next.delete(pidOf(p))
      } else {
        for (const p of rows) {
          const pid = pidOf(p)
          if (pid && !next.has(pid)) next.set(pid, { product: p, qty: 1 })
        }
      }
      return next
    })
  }

  const handleAdd = () => {
    const entries = [...selected.values()].map((x) => ({
      product: x.product,
      qty: Number(x.qty) > 0 ? Number(x.qty) : 1,
    }))
    if (!entries.length) return
    onAdd?.(entries)
  }

  if (!open) return null

  const hasNext = Boolean(data?.meta?.next_page_url)
  const hasPrev = Boolean(data?.meta?.prev_page_url)

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Pilih Produk</h3>
            <p className="text-sm text-neutral-500">
              Centang beberapa produk sekaligus — pilihan tetap tersimpan saat ganti halaman atau kata kunci.
              {selectedUom ? (
                <> Qty diisi dalam satuan <b>{selectedUom}</b>.</>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <input
            ref={searchInput}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value)
              debouncedSetSearch(e.target.value)
            }}
            type="text"
            placeholder="Cari nama produk / SKU..."
            className="w-full py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
          />

          <div className="border rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs font-semibold border-b bg-neutral-50 text-neutral-600">
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={togglePage}
                  title="Pilih semua di halaman ini"
                />
              </div>
              {/* 290726 (laporan user): kolom "UOM produk" DIHAPUS dari deretan kolom.
                  Isinya satuan jual bawaan produk (atribut master, bisa PCS/LSN/PSG),
                  BUKAN satuan input — berdampingan dgn kolom Qty ia terbaca seolah
                  bertentangan dgn satuan yang dipilih di form. Kini jadi keterangan
                  kecil di bawah nama produk. */}
              <div className="col-span-6">Produk</div>
              <div className="col-span-2 text-right">{selectedUom ? `Stok (${selectedUom})` : "Stok"}</div>
              <div className="col-span-3">{selectedUom ? `Qty (${selectedUom})` : "Qty"}</div>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Memuat produk...
                </div>
              ) : !rows.length ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Produk tidak ditemukan.
                </div>
              ) : (
                rows.map((p) => {
                  const pid = pidOf(p)
                  const picked = selected.get(pid)
                  return (
                    <div
                      key={pid}
                      className={`grid grid-cols-12 gap-3 items-center px-4 py-3 text-sm border-b last:border-b-0 cursor-pointer ${
                        picked ? "bg-orange-50" : "hover:bg-neutral-50"
                      }`}
                      onClick={() => toggle(p)}
                    >
                      <div className="col-span-1">
                        <input type="checkbox" checked={Boolean(picked)} readOnly />
                      </div>
                      <div className="col-span-6 min-w-0">
                        <div className="font-medium text-neutral-900 truncate">{labelOf(p)}</div>
                        <div className="text-xs text-neutral-500 truncate">{descOf(p)}</div>
                        {p?.uom ? (
                          <div className="mt-0.5 text-[11px] text-neutral-400">
                            satuan jual: {p.uom}
                          </div>
                        ) : null}
                      </div>
                      <div
                        className="col-span-2 text-right text-xs font-medium text-green-700"
                        title={
                          Number.isFinite(Number(p?.current_stock))
                            ? `${Number(p.current_stock).toLocaleString("id-ID")} PCS`
                            : undefined
                        }
                      >
                        {stockInUom(p)}
                      </div>
                      <div className="col-span-3" onClick={(e) => e.stopPropagation()}>
                        {picked ? (
                          <input
                            type="number"
                            min={1}
                            value={picked.qty}
                            onChange={(e) => setQty(pid, e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border rounded-lg border-neutral-200 focus:outline-none focus:ring"
                          />
                        ) : (
                          <span className="text-xs text-neutral-300">—</span>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-neutral-600">
              <button
                type="button"
                disabled={!hasPrev}
                onClick={() => setPage((x) => Math.max(1, x - 1))}
                className="px-3 py-1.5 border rounded-lg border-neutral-200 disabled:opacity-40"
              >
                ‹ Prev
              </button>
              <span>Hal {page}</span>
              <button
                type="button"
                disabled={!hasNext}
                onClick={() => setPage((x) => x + 1)}
                className="px-3 py-1.5 border rounded-lg border-neutral-200 disabled:opacity-40"
              >
                Next ›
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-neutral-600">
                <b>{selected.size}</b> produk dipilih
              </span>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(new Map())}
                  className="text-xs text-neutral-500 underline"
                >
                  Bersihkan
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs border rounded-xl border-neutral-200 text-neutral-700"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={!selected.size}
                onClick={handleAdd}
                className={`px-4 py-2.5 text-xs font-semibold text-white rounded-xl ${
                  selected.size ? "bg-brand-900 hover:bg-brand-800" : "bg-neutral-400 cursor-not-allowed"
                }`}
              >
                Tambah {selected.size || ""} Produk
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
